import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import type { ViewProps } from 'react-native';
import type { OpenedEvent as NotificationOpenedEvent } from 'react-native-onesignal/src/models/NotificationEvents';

import { getEnvVars } from '@order/utils';

import {
  initOneSignal,
  OneSignal as client,
  resetOneSignalUserSession as resetCustomer,
  startOneSignalUserSession as startCustomerSession,
} from './client';

const { DISABLE_ONESIGNAL_PROMPT } = getEnvVars();

// ────────────────────────────────────────────────────────────────────────────────

const OneSignalContext = createContext<OneSignalContextType | undefined>(
  undefined,
);

/**
 * One Signal (@see https://www.onesignal.com/) provider component.
 */
export const OneSignalProvider = (props: Pick<ViewProps, 'children'>) => {
  const { current: notificationOpenedEventHandlers } =
    useRef<NotificationOpenedEventHandlers>(new Set());

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Handles opened push notifications by forwarding them to the stored event listeners.
   */
  const handleNotificationOpenedEvent = useCallback(
    (event: NotificationOpenedEvent) => {
      if (!notificationOpenedEventHandlers) return;

      // eslint-disable-next-line unicorn/no-array-for-each
      notificationOpenedEventHandlers.forEach((handler) => {
        handler(event);
      });
    },
    [notificationOpenedEventHandlers],
  );

  const init = useCallback(() => {
    (async () => {
      if (DISABLE_ONESIGNAL_PROMPT) return;
      await initOneSignal(handleNotificationOpenedEvent);
    })();
  }, [handleNotificationOpenedEvent]);

  const syncCustomer = useCallback((customer: OneSignalUser) => {
    (async () => {
      if (!customer.id) return;
      await startCustomerSession(customer);
    })();
  }, []);

  const addNotificationOpenedEventListener =
    useCallback<NotificationOpenedEventListener>(
      (handler) => {
        if (notificationOpenedEventHandlers.has(handler)) return;

        notificationOpenedEventHandlers.add(handler);
      },
      [notificationOpenedEventHandlers],
    );

  const removeNotificationOpenedEventListener =
    notificationOpenedEventHandlers.delete;

  // ─────────────────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      client,
      init,
      syncCustomer,
      resetCustomer,
      addNotificationOpenedEventListener,
      removeNotificationOpenedEventListener,
    }),
    [
      addNotificationOpenedEventListener,
      init,
      removeNotificationOpenedEventListener,
      syncCustomer,
    ],
  );

  return (
    <OneSignalContext.Provider value={value}>
      {props.children}
    </OneSignalContext.Provider>
  );
};

/**
 * One Signal (@see https://www.onesignal.com/) client hook.
 * Returns platform specific client and initialized state boolean for future use.
 *
 * ⚠️ Note that the clients APIs can have some differences in case of Web and React Native!
 *    You can use abstract wrapper functions to handle those cases.
 *
 * @example
 * const { client as oneSignalClient } = useOneSignal();
 * const oneSignalUserId = oneSignalClient.getExternalUserId();
 */
export const useOneSignal = () => {
  const context = useContext(OneSignalContext);

  if (context === undefined) {
    throw new Error('useOneSignal must be used within a <OneSignalProvider>');
  }

  return context;
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type OneSignalContextType = Readonly<{
  client: typeof client | undefined;
  init: () => void;
  syncCustomer: (customer: OneSignalUser) => void;
  resetCustomer: () => void;
  addNotificationOpenedEventListener: NotificationOpenedEventListener;
  removeNotificationOpenedEventListener: NotificationOpenedEventListener;
}>;

type OneSignalUser = Readonly<{
  id?: string;
  email?: string;
}>;

type NotificationOpenedEventListener = (
  handler: NotificationOpenedEventHandler,
) => void;

type NotificationOpenedEventHandlers = Set<NotificationOpenedEventHandler>;

type NotificationOpenedEventHandler = (event: NotificationOpenedEvent) => void;
